import { useEffect, useState } from 'react';
import { ShieldCheck, RefreshCw, Check, X, Clock, FileText, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('masterToken');
  return { Authorization: `Bearer ${token}` };
};

const STATUS_STYLES = {
  pending: 'bg-amber-500/15 border-amber-500/30 text-amber-300',
  approved: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
  denied: 'bg-red-500/15 border-red-500/30 text-red-300',
};

export default function MasterEINReviews() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/master/ein-reviews?status=${status}`, { headers: authHeaders() });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load EIN reviews');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const openRow = (row) => { setSelected(row); setNote(''); };
  const closeRow = () => { setSelected(null); setNote(''); };

  const act = async (action) => {
    if (!selected) return;
    if (action === 'deny' && note.trim().length < 4) {
      toast.error('Denial note is required (min 4 chars)');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/master/ein-reviews/${selected.vendor_id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ note: note.trim() }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail || 'Action failed');
      toast.success(action === 'approve' ? 'EIN approved · vendor notified' : 'EIN denied · vendor notified');
      closeRow();
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = (v) => { try { navigator.clipboard.writeText(v); toast.success('Copied'); } catch {} };

  const rows = data?.rows || [];
  const counts = data?.counts || { pending: 0, approved: 0, denied: 0, total: 0 };

  return (
    <div className="space-y-6" data-testid="master-ein-reviews">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-syne text-2xl font-bold text-white flex items-center gap-2">
            <ShieldCheck className="text-emerald-400" size={24} /> EIN Review Queue
          </h1>
          <p className="text-[#9CA3AF] text-sm mt-1">Cross-check every submitted EIN before the vendor carries the Verified Business badge.</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm"
          data-testid="ein-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: 'pending', label: 'Pending', v: counts.pending, color: 'text-amber-400' },
          { k: 'approved', label: 'Approved', v: counts.approved, color: 'text-emerald-400' },
          { k: 'denied', label: 'Denied', v: counts.denied, color: 'text-red-400' },
          { k: 'all', label: 'Total', v: counts.total, color: 'text-[#A78BFA]' },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setStatus(s.k)}
            className={`p-4 rounded-2xl border text-left transition ${status === s.k ? 'bg-white/10 border-white/30' : 'bg-[#1E1E3A] border-white/5 hover:border-white/15'}`}
            data-testid={`ein-filter-${s.k}`}
          >
            <div className="text-[#9CA3AF] text-xs uppercase tracking-wider">{s.label}</div>
            <div className={`text-3xl font-bold mt-1 ${s.color}`}>{s.v}</div>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-white/10 bg-[#1E1E3A] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#9CA3AF]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[#64748B]">
            <FileText className="w-8 h-8 mx-auto mb-2" />
            No {status !== 'all' ? status : ''} EIN submissions.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ein-table">
              <thead className="bg-white/[0.03] text-[#9CA3AF] text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Submitted</th>
                  <th className="px-4 py-3 text-left">Business</th>
                  <th className="px-4 py-3 text-left">Owner</th>
                  <th className="px-4 py-3 text-left">EIN</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.vendor_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-[#9CA3AF] font-mono text-xs">{(r.ein_submitted_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-4 py-3 text-white font-medium">{r.business_name || '—'}</td>
                    <td className="px-4 py-3 text-[#C4B5FD]">{r.owner_name || '—'}</td>
                    <td className="px-4 py-3 text-white font-mono">
                      <button onClick={() => copy(r.ein_number)} className="inline-flex items-center gap-1 hover:text-[#A78BFA]" title="Copy">
                        {r.ein_number?.slice(0, 2)}-{r.ein_number?.slice(2)} <Copy size={12} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold ${STATUS_STYLES[r.ein_review_status] || STATUS_STYLES.pending}`}>
                        <Clock size={11} /> {r.ein_review_status || 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openRow(r)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs font-semibold"
                        data-testid={`ein-review-${r.vendor_id}`}
                      >
                        Review <ExternalLink size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeRow}>
          <div
            className="bg-[#0E0E23] border border-white/10 rounded-2xl p-6 max-w-lg w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="ein-review-modal"
          >
            <div>
              <h2 className="text-white font-syne text-xl font-bold">{selected.business_name || 'Vendor'}</h2>
              <p className="text-[#9CA3AF] text-sm">Owner · {selected.owner_name || '—'}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3">
                <div className="text-[#9CA3AF] text-xs uppercase">EIN</div>
                <div className="text-white font-mono text-lg">{selected.ein_number?.slice(0, 2)}-{selected.ein_number?.slice(2)}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3">
                <div className="text-[#9CA3AF] text-xs uppercase">Location</div>
                <div className="text-white">{selected.location || '—'}</div>
              </div>
            </div>

            <div className="bg-[#1E1E3A] border border-[#8B5CF6]/20 rounded-xl p-3 text-xs text-[#C4B5FD]">
              Cross-check tips: search Google for "<b>{selected.business_name}</b> EIN {selected.ein_number?.slice(0, 2)}-{selected.ein_number?.slice(2)}" or use <a className="underline" href={`https://apps.irs.gov/app/eos/forwardToPub78Download.do`} target="_blank" rel="noreferrer">IRS Pub78</a> / <a className="underline" href={`https://www.einsearch.com`} target="_blank" rel="noreferrer">EINsearch</a>.
            </div>

            <div>
              <label className="text-[#9CA3AF] text-xs uppercase tracking-wider">Reviewer Note (required for Deny)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm focus:outline-none focus:border-[#8B5CF6]"
                placeholder="e.g., EIN matches IRS records for this business name"
                data-testid="ein-review-note"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={closeRow}
                className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={() => act('deny')}
                disabled={busy}
                className="flex-1 py-2.5 rounded-xl bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-200 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="ein-deny-btn"
              >
                <X size={14} /> Deny
              </button>
              <button
                onClick={() => act('approve')}
                disabled={busy}
                className="flex-1 py-2.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="ein-approve-btn"
              >
                <Check size={14} /> Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
